import { useState, useEffect } from 'react';
import { endpoints } from '../config/api';

interface Layer {
  name: string;
  element_count: number;
  types: string[];
  color?: number;
}

interface PreviewData {
  id: string;
  source_file_id: string;
  building_id?: string;
  layers: Layer[];
  elements_summary: {
    [key: string]: number | { [key: string]: number } | undefined;
  };
  bounds: {
    min_x: number;
    max_x: number;
    min_y: number;
    max_y: number;
    min_z?: number;
    max_z?: number;
  };
  georeferencing: {
    lat?: number;
    lon?: number;
    rotation: number;
    scale: number;
  };
  status: string;
  extracted_at?: string;
}

interface CADPreviewProps {
  previewId: string;
  buildingId: string;
  onConfirm: (previewId: string) => void;
  onCancel: () => void;
}

function CADPreview({ previewId, buildingId, onConfirm, onCancel }: CADPreviewProps) {
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Georeferencing state
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [rotation, setRotation] = useState<string>('0');
  const [scale, setScale] = useState<string>('1');

  // Fetch preview data
  useEffect(() => {
    const fetchPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(endpoints.previewById(previewId), {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch preview data');
        }

        const data: PreviewData = await response.json();
        setPreview(data);

        // Initialize georeferencing fields
        if (data.georeferencing.lat) setLatitude(data.georeferencing.lat.toString());
        if (data.georeferencing.lon) setLongitude(data.georeferencing.lon.toString());
        setRotation(data.georeferencing.rotation.toString());
        setScale(data.georeferencing.scale.toString());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [previewId]);

  const handleSaveGeoreferencing = async () => {
    try {
      const response = await fetch(endpoints.previewById(previewId), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          geo_origin_lat: latitude ? parseFloat(latitude) : null,
          geo_origin_lon: longitude ? parseFloat(longitude) : null,
          rotation_degrees: parseFloat(rotation) || 0,
          scale_factor: parseFloat(scale) || 1,
          building_id: buildingId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save georeferencing');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setError(null);

    try {
      // Save georeferencing first
      await handleSaveGeoreferencing();

      // Then confirm and trigger processing
      const response = await fetch(endpoints.previewConfirm(previewId), {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to confirm preview');
      }

      onConfirm(previewId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm');
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="cad-preview-modal">
        <div className="cad-preview-content">
          <div className="loading-spinner">Extracting preview...</div>
        </div>
      </div>
    );
  }

  if (error && !preview) {
    return (
      <div className="cad-preview-modal">
        <div className="cad-preview-content">
          <div className="error-message">{error}</div>
          <button className="cancel-button" onClick={onCancel}>Close</button>
        </div>
      </div>
    );
  }

  if (!preview) return null;

  // Calculate drawing size from bounds
  const width = preview.bounds.max_x - preview.bounds.min_x;
  const height = preview.bounds.max_y - preview.bounds.min_y;

  // Get total element count
  const totalElements = preview.layers.reduce((sum, layer) => sum + layer.element_count, 0);

  // Get detected warehouse elements
  const detectedElements = preview.elements_summary.detected || {};

  return (
    <div className="cad-preview-modal">
      <div className="cad-preview-content">
        <div className="cad-preview-header">
          <h2>CAD Preview</h2>
          <button className="close-button" onClick={onCancel}>&times;</button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="cad-preview-body">
          {/* Left: Layer list */}
          <div className="cad-preview-layers">
            <h3>Layers ({preview.layers.length})</h3>
            <div className="layer-list">
              {preview.layers.map((layer) => (
                <div key={layer.name} className="layer-item">
                  <span className="layer-name" title={layer.name}>
                    {layer.name.length > 25 ? layer.name.substring(0, 25) + '...' : layer.name}
                  </span>
                  <span className="layer-count">{layer.element_count}</span>
                </div>
              ))}
            </div>

            <h3>Summary</h3>
            <div className="summary-stats">
              <div className="stat-row">
                <span>Total Elements:</span>
                <span>{totalElements.toLocaleString()}</span>
              </div>
              <div className="stat-row">
                <span>Drawing Size:</span>
                <span>{width.toFixed(1)} x {height.toFixed(1)}</span>
              </div>
            </div>

            {Object.keys(detectedElements).length > 0 && (
              <>
                <h3>Detected Elements</h3>
                <div className="detected-elements">
                  {Object.entries(detectedElements).map(([type, count]) => (
                    <div key={type} className="detected-item">
                      <span className="detected-type">{type}</span>
                      <span className="detected-count">{count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right: Georeferencing controls */}
          <div className="cad-preview-georef">
            <h3>Georeferencing</h3>
            <p className="georef-hint">
              Set the origin point (lower-left corner of drawing) to position the model on the map.
            </p>

            <div className="georef-form">
              <div className="form-group">
                <label htmlFor="latitude">Latitude</label>
                <input
                  type="number"
                  id="latitude"
                  step="0.000001"
                  placeholder="e.g., 40.4487"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="longitude">Longitude</label>
                <input
                  type="number"
                  id="longitude"
                  step="0.000001"
                  placeholder="e.g., -79.2238"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="rotation">Rotation (degrees from North)</label>
                <input
                  type="number"
                  id="rotation"
                  step="0.1"
                  placeholder="0"
                  value={rotation}
                  onChange={(e) => setRotation(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="scale">Scale Factor</label>
                <input
                  type="number"
                  id="scale"
                  step="0.001"
                  placeholder="1.0"
                  value={scale}
                  onChange={(e) => setScale(e.target.value)}
                />
              </div>
            </div>

            <div className="coordinate-help">
              <strong>Tip:</strong> Click on the 3D viewer to get coordinates for the origin point.
            </div>
          </div>
        </div>

        <div className="cad-preview-footer">
          <button className="cancel-button" onClick={onCancel} disabled={confirming}>
            Cancel
          </button>
          <button
            className="confirm-button"
            onClick={handleConfirm}
            disabled={confirming || !latitude || !longitude}
          >
            {confirming ? 'Processing...' : 'Confirm & Process'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CADPreview;
